import {useCallback, useEffect, useMemo, useState} from "react";
import {useOutletContext, useSearchParams} from "react-router";
import Grid from "@mui/material/Grid2";
import {Box, Paper, Typography, useTheme} from "@mui/material";
import {apiGet} from "../utils";
import {buildV2QueryParams, buildV2BreakdownQueryParams, hasDateFilters, BREAKDOWN_VIEWS} from "../utils/filters";
import {fetchCalcBreakdownMerged} from "../utils/calcBreakdown";
import {resolveChartType} from "../config";
import {KpiSummaryTable} from "../components/KpiSummaryTable";
import {KpiTrendChart} from "../components/KpiTrendChart";
import {MeasureDetailTable} from "../components/MeasureDetailTable";
import {MeasureDetailGroupBySelect, MEASURE_DETAIL_GROUP_BY_OPTIONS} from "../components/MeasureDetailGroupBySelect";
import {LoadingPanel} from "../utils/loading_spinner";

// Always shown in the detail table below, regardless of which top-level KPI
// is selected - these calc types already exist in the shared CALC_CONFIGS
// registry (calc.config.js), so no backend changes are needed.
const EXTRA_FREIGHT_CALC_TYPES = ['freight_burden_percent', 'freight_recapture_rate_percent', 'avg_order_value', 'number_of_orders'];

export const KpiPerformanceDetailContainer = () => {
    const {filters} = useOutletContext();
    const theme = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();

    const [selectedKpi, setSelectedKpi] = useState(null);
    const [kpiSummaryData, setKpiSummaryData] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [detailData, setDetailData] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [trendLoading, setTrendLoading] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [breakdownView, setBreakdownView] = useState(BREAKDOWN_VIEWS.CUSTOMERS);

    useEffect(() => {
        const kpiParam = searchParams.get('kpi') || 'net_revenue';
        setSelectedKpi(resolveChartType(kpiParam));
    }, [searchParams]);

    const handleKpiChange = useCallback((kpiValue) => {
        setSearchParams({kpi: kpiValue});
    }, [setSearchParams]);

    const calcTypes = useMemo(() => {
        const base = Object.keys(kpiSummaryData?.calcConfigs || {});
        // Preserve the original "not ready yet" guard (empty until the KPI
        // summary loads) instead of firing a premature fetch with only the
        // freight columns.
        if (!base.length) {
            return [];
        }
        return [...new Set([...base, ...EXTRA_FREIGHT_CALC_TYPES])];
    }, [kpiSummaryData?.calcConfigs]);

    const fetchSummaryAndTrend = useCallback(() => {
        if (!selectedKpi || !hasDateFilters(filters)) {
            return;
        }

        setSummaryLoading(true);
        setTrendLoading(true);
        const queryParams = buildV2QueryParams(filters);

        apiGet(`v2/calc/${selectedKpi.value}/summary${queryParams}`, (resp) => {
            if (resp.success) {
                setKpiSummaryData(resp.data);
            } else {
                console.error('Summary API error:', resp);
                setKpiSummaryData(null);
            }
            setSummaryLoading(false);
        });

        apiGet(`v2/calc/${selectedKpi.value}/trend${queryParams}`, (resp) => {
            if (resp.success) {
                setChartData(resp.data);
            } else {
                console.error('Trend API error:', resp);
                setChartData(null);
            }
            setTrendLoading(false);
        });
    }, [selectedKpi, filters]);

    const fetchDetailBreakdown = useCallback(() => {
        if (!calcTypes.length || !hasDateFilters(filters)) {
            setDetailData(null);
            return;
        }

        setDetailLoading(true);
        const queryParams = buildV2BreakdownQueryParams(filters, 'current', {breakdownView});

        fetchCalcBreakdownMerged(queryParams, calcTypes, (data) => {
            setDetailData(data);
            setDetailLoading(false);
        });
    }, [calcTypes, filters, breakdownView]);

    useEffect(() => {
        fetchSummaryAndTrend();
    }, [fetchSummaryAndTrend]);

    useEffect(() => {
        fetchDetailBreakdown();
    }, [fetchDetailBreakdown]);

    const handleLoadChildren = useCallback((parentRow, parentGroupBy, onComplete) => {
        if (!calcTypes.length) {
            onComplete({breakdown: [], groupBy: null});
            return;
        }

        const accountId = Number(parentRow.id);
        if (!Number.isFinite(accountId)) {
            onComplete({breakdown: [], groupBy: null});
            return;
        }

        let childBreakdownView;
        if (parentGroupBy === 'hq_account_id') {
            childBreakdownView = BREAKDOWN_VIEWS.CUSTOMERS;
        } else if (parentGroupBy === 'account_id') {
            childBreakdownView = BREAKDOWN_VIEWS.PRODUCTS;
        } else {
            onComplete({breakdown: [], groupBy: null});
            return;
        }

        const queryParams = buildV2BreakdownQueryParams(filters, 'current', {
            accountId,
            breakdownView: childBreakdownView,
        });

        fetchCalcBreakdownMerged(queryParams, calcTypes, (data) => {
            onComplete({
                breakdown: data?.breakdown || [],
                groupBy: data?.groupBy || null,
            });
        });
    }, [calcTypes, filters]);

    const detailGroupBy = detailData?.groupBy || 'hq_account_id';
    const detailTitle = MEASURE_DETAIL_GROUP_BY_OPTIONS.find(
        (option) => option.value === breakdownView
    )?.label || 'Customers';

    const pageLoading = summaryLoading && trendLoading && !kpiSummaryData;

    if (pageLoading) {
        return (
            <LoadingPanel
                loading={true}
                message={`Loading KPI performance data for ${selectedKpi?.name}...`}
            />
        );
    }

    return (
        <div style={{marginTop: 30, marginBottom: 100, marginLeft: 30, marginRight: 30, maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden'}}>
            <Grid container spacing={3} style={{marginBottom: 30}}>
                <Grid size={6}>
                    <KpiSummaryTable
                        kpi={selectedKpi}
                        summary={kpiSummaryData?.summary}
                        calcConfigs={kpiSummaryData?.calcConfigs}
                        onKpiChange={handleKpiChange}
                    />
                </Grid>
                <Grid size={6}>
                    <KpiTrendChart
                        title={selectedKpi?.name}
                        valueField={selectedKpi?.value}
                        data={chartData?.currentTrend?.results}
                        previousPeriodData={chartData?.priorTrend?.results}
                        filters={filters}
                    />
                </Grid>
            </Grid>

            <Paper sx={{...theme.palette.contentPanel, borderRadius: 2, p: 2, mt: 4, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2}}>
                    <Typography variant="h6" color="primary">
                        {detailTitle}
                    </Typography>
                    <MeasureDetailGroupBySelect
                        value={breakdownView}
                        onChange={setBreakdownView}
                    />
                </Box>

                <MeasureDetailTable
                    breakdown={detailData?.breakdown}
                    calcConfigs={detailData?.calcConfigs}
                    groupBy={detailGroupBy}
                    loading={detailLoading}
                    onLoadChildren={handleLoadChildren}
                />
            </Paper>
        </div>
    );
};
